import {
  faClone,
  faDistributeSpacingVertical,
  faPlus,
  faRectangleVertical,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { ListItem as MenuItem } from 'src/components/ListItem';
import { Meta } from 'src/components/Meta';
import { SearchParamsPagination } from 'src/components/Pagination';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import {
  deleteAppAlert,
  duplicateAppAlert,
  fetchAdminAppAlerts,
} from 'src/models/app-alert/appalerts';
import { supportsBanners } from 'src/models/platform/utils';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import api from 'src/utils/ajax';
import { formatFullDate } from 'src/utils/dateFormatter';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { AppAlertType } from './NewAppAlert/_types';

import type { AppAlert } from 'src/models/app-alert/_types';

const AppAlertsIndexPage: React.FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appAlertsIndex',
  });
  const { session } = useOptionalSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDialogProps, confirm] = useConfirmDialogComponent();
  const { params, updateParam } = useListSearchParams();

  const { data, isLoading } = useQuery({
    queryKey: [
      'adminAppAlerts',
      params.page,
      params.perPage,
      params.search,
      params.sortBy,
      params.sortDir,
    ],
    queryFn: () =>
      fetchAdminAppAlerts({
        page: params.page,
        perPage: params.perPage,
        search: params.search || undefined,
        sortBy: params.sortBy || undefined,
        sortDir: params.sortBy ? params.sortDir : undefined,
      }),
  });

  const alerts = data?.alerts || [];
  const totalAlerts = data?.total || 0;

  const { mutateAsync: deleteAlertMutation } = useMutation({
    mutationFn: deleteAppAlert,
    onSuccess: async () => {
      toast.success(
        t('deleteSuccess', {
          defaultValue: 'Alert has been successfully deleted!',
        })
      );
      await queryClient.invalidateQueries({
        queryKey: ['adminAppAlerts'],
      });
    },
    onError: (error) => {
      toast.error(
        api.formatError(
          error,
          t('deleteAutomationError', {
            defaultValue:
              'You cannot delete alerts that are tied to automations',
          })
        )
      );
    },
  });

  const { mutateAsync: duplicateAlertMutation } = useMutation({
    mutationFn: duplicateAppAlert,
    onSuccess: async () => {
      toast.success(
        t('duplicateSuccess', {
          defaultValue: 'Alert duplicated',
        })
      );
      await queryClient.invalidateQueries({
        queryKey: ['adminAppAlerts'],
      });
    },
    onError: (error) => {
      toast.error(
        api.formatError(
          error,
          t('duplicateFailed', {
            defaultValue: 'Failed to duplicate app alert',
          })
        )
      );
    },
  });

  const columns = [
    {
      id: 'title',
      label: t('columnHeaders.title', {
        defaultValue: 'Title',
      }),
      sortable: true,
      sortKey: 'title',
    },
    {
      id: 'createdAt',
      label: t('columnHeaders.created', {
        defaultValue: 'Created',
      }),
      sortable: true,
      sortKey: 'created_at',
      format: (value) => formatFullDate(value),
    },
    {
      id: 'alertType',
      label: t('columnHeaders.style', {
        defaultValue: 'Style',
      }),
      sortable: true,
      sortKey: 'alert_type',
      format: (value: AppAlert['alertType']) =>
        t(`types.${value}`, {
          defaultValue: value === 'banner' ? 'Banner' : 'Modal',
        }),
    },
  ];

  const handleNavigateToForm = (alertType: AppAlertType) => {
    navigate(`/admin/modal-alerts/new?alertType=${alertType}`);
  };

  const handleNavigateToEdit = (alertId: AppAlert['id']) => {
    navigate(`/admin/modal-alerts/${alertId}/edit`);
  };

  const handleDelete = async (alert: AppAlert) => {
    const confirmed = await confirm({
      title: t('actions.delete', {
        defaultValue: 'Delete',
      }),
      message: t('delete', {
        title: alert.title,
        defaultValue: 'Are you sure you want to delete {{title}}?',
      }),
      confirmText: t('actions.delete', {
        defaultValue: 'Delete',
      }),
      cancelText: t('cancel', {
        defaultValue: 'Cancel',
      }),
    });

    if (!confirmed) {
      return;
    }

    await deleteAlertMutation(alert.id);
  };

  const rowActions = [
    {
      icon: faTrash,
      label: t('actions.delete', {
        defaultValue: 'Delete',
      }),
      onClick: (row: AppAlert) => {
        void handleDelete(row);
      },
    },
    {
      icon: faClone,
      label: t('actions.duplicate', {
        defaultValue: 'Duplicate',
      }),
      onClick: (row: AppAlert) => {
        void duplicateAlertMutation(row);
      },
    },
  ];

  const buttonLabel = t('new', {
    defaultValue: 'New App Alert',
  });
  const countLabel =
    totalAlerts === 0
      ? t('countZero', {
          defaultValue: 'No app alerts',
        })
      : totalAlerts === 1
        ? t('count', {
            count: totalAlerts,
            defaultValue: '{{count}} App Alert',
          })
        : t('countOther', {
            count: totalAlerts,
            defaultValue: '{{count}} App Alerts',
          });

  const newAlertAction =
    !session || !supportsBanners(session) ? (
      <Button
        color="primary"
        variant="prominent"
        startIcon={<Icon icon={faPlus} />}
        onClick={() => handleNavigateToForm('modal')}
      >
        {buttonLabel}
      </Button>
    ) : (
      <DropdownMenu
        drawerOnMobile
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        trigger={
          <Button
            color="primary"
            variant="prominent"
            startIcon={<Icon icon={faPlus} />}
          >
            {buttonLabel}
          </Button>
        }
      >
        <Box sx={{ minWidth: 220 }}>
          <MenuItem
            clickable
            icon={faRectangleVertical}
            primary={t('types.modalFullScreen', {
              defaultValue: 'Modal (Full Screen)',
            })}
            onClick={() => handleNavigateToForm('modal')}
          />
          <MenuItem
            clickable
            icon={faDistributeSpacingVertical}
            primary={t('types.banner', {
              defaultValue: 'Banner',
            })}
            onClick={() => handleNavigateToForm('banner')}
          />
        </Box>
      </DropdownMenu>
    );

  return (
    <>
      <Meta
        title={t('metaTitle', { defaultValue: 'App Alerts' })}
        brand={session?.brand}
      />
      <AppFrameConfig
        id="admin-modal-alerts-index"
        containerMaxWidth="xl"
        title={t('manage', {
          defaultValue: 'Manage App Alerts',
        })}
        actions={newAlertAction}
      />
      <ConfirmDialog {...confirmDialogProps} />
      <Stack spacing={3}>
        <SectionToolbar sx={{ px: 0, py: 0 }}>
          <SearchParamSearchTextField
            params={params}
            updateParam={updateParam}
            placeholder={t('find', {
              defaultValue: 'Find App Alerts',
            })}
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
        </SectionToolbar>

        <Typography variant="body2" color="text.secondary">
          {countLabel}
        </Typography>

        <Card>
          <DataTable
            columns={columns}
            rows={alerts}
            loading={isLoading}
            rowActions={rowActions}
            onClickRow={(row) => handleNavigateToEdit((row as AppAlert).id)}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            setSortBy={(sortBy) => {
              if (typeof sortBy !== 'function') {
                updateParam('sortBy', sortBy || null);
              }
            }}
            setSortDir={(sortDir) => {
              if (typeof sortDir !== 'function') {
                updateParam('sortDir', sortDir);
              }
            }}
            noResultsText={t('noResults', {
              defaultValue: 'No App Alerts found',
            })}
            fixedActions
          />
        </Card>
        <SearchParamsPagination
          total={totalAlerts}
          params={params}
          updateParam={updateParam}
        />
      </Stack>
    </>
  );
};

export default AppAlertsIndexPage;
